import React, { useMemo, memo, useRef } from 'react'
import {
  useCards,
  useCategories,
  useContentAccess,
  useFavorites,
  useAddToFavorites,
  useRemoveFromFavorites,
} from '../../hooks/useApi'
import { useAppStore } from '../../store/useAppStore'
import { CategoryGridProps, CategoryNode, ShowingType } from './types'
import { findCategoryById } from './utils'
import { useFiltering } from './useFiltering'
import LoadingSkeleton from './LoadingSkeleton'
import EmptyState from './EmptyState'
import InteractiveCategoryCard from './InteractiveCategoryCard'
import CategoryBlock from './CategoryBlock'
import './CategoryGrid.css'
import { flushSync } from 'react-dom'
import { SUBSCRIPTION_TEASERS } from '../../constants/subscriptionTeasers'

const CategoryGrid = memo(
  ({
    section,
    searchQuery,
    onSectionChange,
    onSubsectionChange,
    onSubSubsectionChange,
  }: CategoryGridProps) => {
    // Реф для прокрутки к началу списка
    const gridRef = useRef<HTMLDivElement>(null)

    // Функция для плавной прокрутки к началу блока category-grid
    const scrollToTop = () => {
      // Функция для попытки прокрутки с несколькими методами поиска элемента
      const attemptScroll = () => {
        // Метод 1: Поиск SectionTabs (приоритет - чтобы были видны табы, поиск и карточки)
        const sectionTabsElement = document.querySelector('.section-tabs')
        if (sectionTabsElement) {
          sectionTabsElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          })
          return true
        }

        // Метод 2: Поиск SearchBar (если нет табов)
        const searchBarElement = document.querySelector('.search-bar')
        if (searchBarElement) {
          searchBarElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          })
          return true
        }

        // Метод 3: Через ref (CategoryGrid)
        if (gridRef.current) {
          gridRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          })
          return true
        }

        // Метод 4: Поиск CategoryGrid по классу
        const gridElement = document.querySelector('.category-grid')
        if (gridElement) {
          gridElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          })
          return true
        }

        return false
      }

      try {
        // Пробуем сразу
        if (attemptScroll()) {
          return
        }

        // Если не получилось, ждем немного и пробуем снова
        setTimeout(() => {
          if (attemptScroll()) {
            return
          }

          // Если все еще не получилось, ждем еще немного
          setTimeout(() => {
            if (!attemptScroll()) {
              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              })
            }
          }, 200)
        }, 50)
      } catch (error) {
        console.error('Scroll error:', error)
        // Fallback - обычная прокрутка без анимации
        window.scrollTo(0, 0)
      }
    }

    // Все хуки должны быть вызваны в начале
    const { data: cards, isLoading: cardsLoading } = useCards()
    const { hasSubscription } = useContentAccess()
    const { favorites } = useAppStore()
    const { data: categories, isLoading: categoriesLoading } =
      useCategories(hasSubscription)
    const {
      filterSubcategories,
      filterCards,
      filterAllCards,
      getTotalCardCount,
      filterCategoriesWithCards,
    } = useFiltering()

    // API мутации для избранного
    const addToFavoritesMutation = useAddToFavorites()
    const removeFromFavoritesMutation = useRemoveFromFavorites()

    // Загружаем избранное при монтировании компонента
    useFavorites()

    // ТЕПЕРЬ определяем isLoading после всех хуков
    const isLoading = cardsLoading || (hasSubscription && categoriesLoading)

    // Функция для переключения избранного с API
    const handleToggleFavorite = async (itemId: string) => {
      const cardId = parseInt(itemId)
      const isFavorite = favorites.includes(itemId)

      try {
        if (isFavorite) {
          await removeFromFavoritesMutation.mutateAsync({ card_id: cardId })
        } else {
          await addToFavoritesMutation.mutateAsync({ card_id: cardId })
        }
      } catch (error) {
        console.error('Ошибка при изменении избранного:', error)
      }
    }

    // Мемоизируем выбранную категорию
    const selectedCategory = useMemo(() => {
      if (!section || !categories?.categories) return null
      return findCategoryById(categories.categories as CategoryNode[], section)
    }, [section, categories])

    // Проверка готовности данных
    const isDataReady = useMemo(() => {
      if (isLoading) return false
      if (hasSubscription && !categories?.categories) return false
      if (!cards?.cards) return false
      return true
    }, [isLoading, hasSubscription, categories, cards])

    // Показываем загрузку если данные не готовы
    if (!isDataReady) {
      return <LoadingSkeleton />
    }

    // Если есть поисковый запрос, показываем результаты поиска по всем карточкам
    if (searchQuery && searchQuery.trim()) {
      if (!cards?.cards) {
        return <EmptyState message="Карточки не найдены" />
      }

      const filteredCards = filterAllCards(cards.cards, searchQuery)
      const totalResults = filteredCards.length

      return (
        <div className="category-grid">
          <div className="section-header">
            <h2>Результаты поиска</h2>
            <span className="search-results-count">
              {totalResults}{' '}
              {totalResults === 1
                ? 'результат'
                : totalResults < 5
                  ? 'результата'
                  : 'результатов'}
            </span>
          </div>

          {/* Найденные карточки. Отдельного подзаголовка нет: в «Базе»
              ищется только библиотека, делить результат не на что. */}
          {filteredCards.length > 0 && (
            <div className="categories-grid final-cards-grid">
              {filteredCards.map(card => {
                const cardId = card.card_id.toString()
                return (
                  <InteractiveCategoryCard
                    key={cardId}
                    item={card}
                    showingType="cards"
                    hasSubscription={hasSubscription}
                    onToggleFavorite={handleToggleFavorite}
                    isFavorite={favorites.includes(cardId)}
                    searchQuery={searchQuery}
                  />
                )
              })}
            </div>
          )}

          {totalResults === 0 && (
            <div className="empty-state">
              <p>По запросу "{searchQuery}" ничего не найдено</p>
              <p>Попробуйте изменить поисковый запрос</p>
            </div>
          )}
        </div>
      )
    }

    // Если нет подписки - показываем простой список всех карточек под одним блоком
    if (!hasSubscription) {
      if (!cards?.cards) {
        return <EmptyState message="Карточки не найдены" />
      }

      const filteredCards = filterAllCards(cards.cards, searchQuery)

      return (
        <div className="category-grid">
          <div className="grouped-categories">
            <CategoryBlock
              title="Доступный материал"
              categoryId={0}
              items={filteredCards}
              itemType="cards"
              className="category-block available-content-block"
              isAvailableContent={true}
              onToggleFavorite={handleToggleFavorite}
              favorites={favorites}
              hasSubscription={hasSubscription}
              searchQuery={searchQuery}
              onScrollToTop={scrollToTop}
            />
            {/* Материалы в подписке: ручная подборка 10 карточек */}
            {!hasSubscription && (
              <CategoryBlock
                title="Материалы в подписке"
                categoryId={-1}
                items={SUBSCRIPTION_TEASERS}
                itemType="cards"
                maxItems={10}
                className="category-block available-content-block"
                isAvailableContent={true}
                lockContent={true}
                onToggleFavorite={handleToggleFavorite}
                favorites={favorites}
                hasSubscription={hasSubscription}
                searchQuery={searchQuery}
                onScrollToTop={scrollToTop}
              />
            )}
          </div>
        </div>
      )
    }

    // Проверяем категории только для пользователей с подпиской
    if (hasSubscription && !categories?.categories) {
      return <EmptyState message="Категории не найдены" />
    }

    // Если ничего не выбрано, показываем группированный вид
    if (!section) {
      if (!categories?.categories) {
        return <EmptyState message="Категории не найдены" />
      }

      const mainCategories = (categories.categories as CategoryNode[])
        .filter(cat => cat.level === 1)
        .filter(cat => getTotalCardCount(cat) > 0)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

      return (
        <div className="category-grid">
          <div className="grouped-categories">
            {mainCategories.map(mainCategory => {
              const subcategories = mainCategory.subcategories || []
              const filteredSubcategories = filterSubcategories(
                subcategories,
                searchQuery,
                mainCategory.name,
                cards?.cards
              )

              const hasCards =
                mainCategory.card_ids && mainCategory.card_ids.length > 0
              if (filteredSubcategories.length === 0 && !hasCards) {
                return null
              }

              let itemsToShow: any[] = filteredSubcategories
              let itemType: 'subcategories' | 'cards' = 'subcategories'

              if (
                filteredSubcategories.length === 0 &&
                hasCards &&
                cards?.cards
              ) {
                itemType = 'cards'
                itemsToShow = filterCards(
                  cards.cards,
                  mainCategory.card_ids!,
                  searchQuery,
                  mainCategory.name
                )
              }

              return (
                <CategoryBlock
                  key={mainCategory.category_id}
                  title={mainCategory.name}
                  categoryId={mainCategory.category_id}
                  items={itemsToShow}
                  itemType={itemType}
                  onToggleFavorite={handleToggleFavorite}
                  favorites={favorites}
                  hasSubscription={hasSubscription}
                  searchQuery={searchQuery}
                  onScrollToTop={scrollToTop}
                  onCategoryClick={categoryId => {
                    const clickedCategory = categories?.categories
                      ? findCategoryById(
                          categories.categories as CategoryNode[],
                          categoryId.toString()
                        )
                      : null

                    if (clickedCategory?.level === 1 && onSectionChange) {
                      onSectionChange(categoryId.toString())
                    } else if (
                      clickedCategory?.level === 2 &&
                      onSubsectionChange
                    ) {
                      if (
                        onSectionChange &&
                        clickedCategory.parent_category_id
                      ) {
                        onSectionChange(
                          clickedCategory.parent_category_id.toString()
                        )
                      }
                      onSubsectionChange(categoryId.toString())
                    }
                  }}
                  onSeeAllClick={categoryId => {
                    if (onSectionChange) {
                      onSectionChange(categoryId.toString())
                    }
                    if (onSubsectionChange) {
                      onSubsectionChange('')
                    }
                  }}
                />
              )
            })}
          </div>
        </div>
      )
    }

    // Если выбрана категория но она еще не найдена - показываем загрузку
    if (!selectedCategory) {
      return <LoadingSkeleton />
    }

    // Обычная логика для выбранной категории
    let itemsToShow: any[] = []
    let showingType: ShowingType = 'subcategories'
    let selectedCategoryNode: CategoryNode | null = null

    // Находим выбранную категорию в дереве
    selectedCategoryNode = categories?.categories
      ? findCategoryById(categories.categories as CategoryNode[], section)
      : null

    if (selectedCategoryNode) {
      // Если это категория level 1 (главная)
      if (selectedCategoryNode.level === 1) {
        const subcategories = selectedCategoryNode.subcategories || []
        // Фильтруем подкатегории, которые имеют карточки
        const subcategoriesWithCards = filterCategoriesWithCards(subcategories)

        // Если у категории level 1 нет подкатегорий, но есть карточки - показываем их напрямую
        if (
          subcategoriesWithCards.length === 0 &&
          selectedCategoryNode.card_ids &&
          selectedCategoryNode.card_ids.length > 0 &&
          cards?.cards
        ) {
          const filteredCards = filterCards(
            cards.cards,
            selectedCategoryNode.card_ids,
            searchQuery,
            selectedCategoryNode.name
          )

          return (
            <div className="category-grid">
              <div className="categories-grid final-cards-grid">
                {filteredCards.map(card => {
                  const cardId = card.card_id.toString()
                  return (
                    <InteractiveCategoryCard
                      key={cardId}
                      item={card}
                      showingType="cards"
                      hasSubscription={hasSubscription}
                      onToggleFavorite={handleToggleFavorite}
                      isFavorite={favorites.includes(cardId)}
                      searchQuery={searchQuery}
                    />
                  )
                })}
              </div>
            </div>
          )
        }

        return (
          <div className="category-grid">
            <div className="grouped-categories">
              {/* Материалы в подписке: ручная подборка 10 карточек */}

              {subcategoriesWithCards.map(subcategory => {
                const subSubcategories = subcategory.subcategories || []

                // Фильтрация по поиску
                let filteredItems: any[] = []
                let itemType: 'subcategories' | 'cards' = 'subcategories'

                if (subSubcategories.length > 0) {
                  // Если есть под-подкатегории, показываем их
                  itemType = 'subcategories'
                  filteredItems = filterSubcategories(
                    subSubcategories,
                    searchQuery,
                    subcategory.name,
                    cards?.cards
                  )
                } else if (
                  subcategory.card_ids &&
                  subcategory.card_ids.length > 0 &&
                  cards?.cards
                ) {
                  // Если нет под-подкатегорий, но есть карточки
                  itemType = 'cards'
                  filteredItems = filterCards(
                    cards.cards,
                    subcategory.card_ids,
                    searchQuery,
                    subcategory.name
                  )
                }

                // Показываем блок только если есть элементы и они проходят фильтр
                if (filteredItems.length === 0) {
                  return null
                }

                return (
                  <CategoryBlock
                    key={subcategory.category_id}
                    title={subcategory.name}
                    categoryId={subcategory.category_id}
                    items={filteredItems}
                    itemType={itemType}
                    onToggleFavorite={handleToggleFavorite}
                    favorites={favorites}
                    hasSubscription={hasSubscription}
                    searchQuery={searchQuery}
                    onScrollToTop={scrollToTop}
                    onCategoryClick={categoryId => {
                      // Для подкатегорий уровня 2 вызываем onSubsectionChange

                      if (itemType === 'subcategories' && onSubsectionChange) {
                        // Найдем категорию и её родителя
                        const clickedCategory = categories?.categories
                          ? findCategoryById(
                              categories.categories as CategoryNode[],
                              categoryId.toString()
                            )
                          : null

                        if (clickedCategory && clickedCategory.level === 2) {
                          // Сначала устанавливаем родительскую категорию (section)
                          if (
                            onSectionChange &&
                            clickedCategory.parent_category_id
                          ) {
                            onSectionChange(
                              clickedCategory.parent_category_id.toString()
                            )
                          }

                          // Используем setTimeout для задержки установки subsection
                          setTimeout(() => {
                            onSubsectionChange(categoryId.toString())
                          }, 0)
                        } else if (
                          clickedCategory &&
                          clickedCategory.level === 3
                        ) {
                          // Для level 3 категорий нужно найти level 1 родителя
                          const parentLevel2 = categories?.categories
                            ? findCategoryById(
                                categories.categories as CategoryNode[],
                                clickedCategory.parent_category_id?.toString() ||
                                  ''
                              )
                            : null

                          if (
                            parentLevel2 &&
                            onSectionChange &&
                            onSubsectionChange &&
                            onSubSubsectionChange
                          ) {
                            // Устанавливаем section (level 1)
                            onSectionChange(
                              parentLevel2.parent_category_id?.toString() || ''
                            )

                            // Устанавливаем subsection (level 2)
                            setTimeout(() => {
                              onSubsectionChange(
                                clickedCategory.parent_category_id?.toString() ||
                                  ''
                              )
                            }, 0)

                            // Устанавливаем subSubsection (level 3)
                            setTimeout(() => {
                              onSubSubsectionChange?.(categoryId.toString())
                            }, 50)
                          }
                        }
                      }
                    }}
                    onSeeAllClick={categoryId => {
                      // Определяем уровень категории для правильного обработчика
                      const clickedCategory = categories?.categories
                        ? findCategoryById(
                            categories.categories as CategoryNode[],
                            categoryId.toString()
                          )
                        : null

                      if (clickedCategory?.level === 2 && onSubsectionChange) {
                        // Для level 2 - устанавливаем subsection
                        onSubsectionChange(categoryId.toString())
                      } else if (
                        clickedCategory?.level === 3 &&
                        onSubSubsectionChange
                      ) {
                        // Для level 3 - нужно установить всю цепочку
                        const parentLevel2 = categories?.categories
                          ? findCategoryById(
                              categories.categories as CategoryNode[],
                              clickedCategory.parent_category_id?.toString() ||
                                ''
                            )
                          : null

                        if (
                          parentLevel2 &&
                          onSectionChange &&
                          onSubsectionChange
                        ) {
                          // Устанавливаем section (level 1)
                          onSectionChange(
                            parentLevel2.parent_category_id?.toString() || ''
                          )

                          // Устанавливаем subsection (level 2)
                          setTimeout(() => {
                            onSubsectionChange(
                              clickedCategory.parent_category_id?.toString() ||
                                ''
                            )
                          }, 0)

                          // Устанавливаем subSubsection (level 3)
                          setTimeout(() => {
                            onSubSubsectionChange(categoryId.toString())
                          }, 50)
                        }
                      }
                    }}
                  />
                )
              })}
            </div>
          </div>
        )
      } else if (selectedCategoryNode.level === 2) {
        // Для категорий level 2 показываем группированный вид подкатегорий с карточками
        const subcategories = selectedCategoryNode.subcategories || []
        // Фильтруем подкатегории, которые имеют карточки
        const subcategoriesWithCards = filterCategoriesWithCards(subcategories)

        if (subcategoriesWithCards.length > 0) {
          return (
            <div className="category-grid">
              <div className="grouped-categories">
                {subcategoriesWithCards.map(subcategory => {
                  // Для level 3 категорий показываем карточки
                  let filteredCards: any[] = []

                  if (
                    subcategory.card_ids &&
                    subcategory.card_ids.length > 0 &&
                    cards?.cards
                  ) {
                    filteredCards = filterCards(
                      cards.cards,
                      subcategory.card_ids,
                      searchQuery,
                      subcategory.name
                    )
                  }

                  // Показываем блок только если есть карточки и они проходят фильтр
                  if (filteredCards.length === 0) {
                    return null
                  }

                  return (
                    <CategoryBlock
                      key={subcategory.category_id}
                      title={subcategory.name}
                      categoryId={subcategory.category_id}
                      items={filteredCards}
                      itemType="cards"
                      onToggleFavorite={handleToggleFavorite}
                      favorites={favorites}
                      hasSubscription={hasSubscription}
                      searchQuery={searchQuery}
                      onScrollToTop={scrollToTop}
                      onCategoryClick={categoryId => {
                        // Карточки не нуждаются в обработчике, они переходят по ссылке
                      }}
                      onSeeAllClick={categoryId => {
                        // Это level 3 категория, нужно установить subSubsection
                        if (onSubSubsectionChange) {
                          onSubSubsectionChange(categoryId.toString())
                        }
                      }}
                    />
                  )
                })}
              </div>
            </div>
          )
        } else {
          // Если у level 2 категории нет подкатегорий, но есть карточки напрямую
          const hasCards =
            selectedCategoryNode.card_ids &&
            selectedCategoryNode.card_ids.length > 0

          if (hasCards && cards?.cards) {
            showingType = 'cards'
            itemsToShow = filterCards(
              cards.cards,
              selectedCategoryNode.card_ids!,
              searchQuery
            )
          }
        }
      } else {
        // Для категорий level 3+ показываем подкатегории или карточки как раньше
        const hasSubcategories =
          selectedCategoryNode.subcategories &&
          selectedCategoryNode.subcategories.length > 0
        const hasCards =
          selectedCategoryNode.card_ids &&
          selectedCategoryNode.card_ids.length > 0

        if (hasSubcategories) {
          // Показываем подкатегории
          showingType = 'subcategories'
          itemsToShow = filterSubcategories(
            selectedCategoryNode.subcategories!,
            searchQuery,
            undefined,
            cards?.cards
          )
        } else if (hasCards && cards?.cards) {
          // Показываем карточки только если нет подкатегорий
          showingType = 'cards'
          itemsToShow = filterCards(
            cards.cards,
            selectedCategoryNode.card_ids!,
            searchQuery
          )
        }
      }
    }

    const getSectionTitle = () => {
      if (selectedCategoryNode) {
        return selectedCategoryNode.name
      }
      return 'Категории'
    }

    const handleSeeAllClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()

      // Прокручиваем к началу списка
      scrollToTop()

      // При клике на "Все" показываем только главную категорию
      if (selectedCategoryNode && selectedCategoryNode.level !== undefined) {
        if (selectedCategoryNode.level === 1) {
          // Если уже на level 1, просто перезагружаем
          if (onSectionChange) {
            onSectionChange(selectedCategoryNode.category_id.toString())
          }
        } else if (selectedCategoryNode.level === 2) {
          // Если на level 2, переходим к родительской категории level 1
          if (onSectionChange && selectedCategoryNode.parent_category_id) {
            onSectionChange(selectedCategoryNode.parent_category_id.toString())
          }
        } else if (selectedCategoryNode.level >= 3) {
          // Если на level 3+, ищем родительскую категорию level 1
          let parentCategory = selectedCategoryNode
          while (
            parentCategory &&
            parentCategory.level !== undefined &&
            parentCategory.level > 1
          ) {
            const parent = categories?.categories
              ? findCategoryById(
                  categories.categories as CategoryNode[],
                  parentCategory.parent_category_id?.toString() || ''
                )
              : null
            if (parent) {
              parentCategory = parent
            } else {
              break
            }
          }
          if (parentCategory && parentCategory.level === 1 && onSectionChange) {
            onSectionChange(parentCategory.category_id.toString())
          }
        }
      }

      // Очищаем subsection
      if (onSubsectionChange) {
        onSubsectionChange('')
      }
    }

    return (
      <div className="category-grid" ref={gridRef}>
        <div className="section-header">
          <h2>{getSectionTitle()}</h2>
          {showingType !== 'cards' && (
            <button
              type="button"
              onClick={handleSeeAllClick}
              className="see-all-link"
            >
              Все
            </button>
          )}
        </div>

        <div
          className={
            showingType === 'cards'
              ? 'categories-grid final-cards-grid'
              : 'categories-grid'
          }
        >
          {itemsToShow.map(item => {
            const itemId = (
              showingType === 'subcategories' ? item.category_id : item.card_id
            ).toString()
            return (
              <InteractiveCategoryCard
                key={itemId}
                item={item}
                showingType={showingType}
                hasSubscription={hasSubscription}
                onToggleFavorite={
                  showingType === 'cards' ? handleToggleFavorite : undefined
                }
                isFavorite={
                  showingType === 'cards' ? favorites.includes(itemId) : false
                }
                searchQuery={searchQuery}
                onCategoryClick={categoryId => {
                  const clickedCategory = categories?.categories
                    ? findCategoryById(
                        categories.categories as CategoryNode[],
                        categoryId.toString()
                      )
                    : null

                  if (!clickedCategory) return

                  // Прокручиваем к началу списка при клике на карточку
                  scrollToTop()

                  // Используем flushSync для батчинга обновлений
                  if (clickedCategory.level === 1 && onSectionChange) {
                    onSectionChange(categoryId.toString())
                  } else if (
                    clickedCategory.level === 2 &&
                    onSubsectionChange &&
                    onSectionChange
                  ) {
                    // Вызываем только onSubsectionChange, она обновит весь state
                    if (clickedCategory.parent_category_id) {
                      // В HomePage handleSubsectionChange должен сохранять текущий section
                      onSubsectionChange(categoryId.toString())
                    }
                  }
                }}
              />
            )
          })}
        </div>

        {itemsToShow.length === 0 && (
          <div className="empty-state">
            <p>
              {showingType === 'subcategories'
                ? 'Подкатегории не найдены'
                : 'Карточки не найдены'}
            </p>
          </div>
        )}
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Кастомная функция сравнения для React.memo
    // Предотвращаем ре-рендер если основные пропсы не изменились
    return (
      prevProps.section === nextProps.section &&
      prevProps.searchQuery === nextProps.searchQuery &&
      prevProps.onSectionChange === nextProps.onSectionChange &&
      prevProps.onSubsectionChange === nextProps.onSubsectionChange &&
      prevProps.onSubSubsectionChange === nextProps.onSubSubsectionChange
    )
  }
)

export default CategoryGrid
